import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User as UserIcon, UserPlus } from 'lucide-react';
import { useAuth } from '@/app/providers';
import { Button, Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { ROUTES } from '@/config/constants';
import { ToolkitMenu } from '@/components/layout/ToolkitMenu';
import { AvatarMenu } from '@/features/profile/components/AvatarMenu';
import { useAddFriend, useFriendship } from '@/features/profile/hooks/useSocial';
import { useAuthModal } from '@/features/auth/authModalStore';
import type { Profile } from '@/types';
import { DirectMessageThread } from './DirectMessageThread';
import { FriendshipInfoRail } from './FriendshipInfoRail';

/**
 * The one and only direct-message surface. Both the `/dm/:id` route and the
 * "message" action from a profile render this, so the chat looks and behaves
 * identically no matter how the user got here — same header, break line,
 * thread, and friendship rail. Pass a resolved `peer` (or null while loading /
 * not found) and the conversation id (undefined until the two are friends).
 */
export function DirectMessageScreen({
  peer,
  conversationId,
  loading = false,
}: {
  peer: Profile | null;
  conversationId: string | undefined;
  loading?: boolean;
}) {
  const navigate = useNavigate();
  const { user, profile, loading: authLoading, signOut } = useAuth();
  const openAuth = useAuthModal((s) => s.openAuth);

  const peerName = peer?.display_name || peer?.handle || 'this member';
  const { data: friendship } = useFriendship(user?.id, peer?.id);
  const addFriend = useAddFriend(user?.id, peer?.id);
  const canAddFriend = Boolean(user && peer && peer.id !== user.id && !friendship);

  function addAsFriend() {
    if (!canAddFriend) return;
    addFriend.mutate(undefined, {
      onSuccess: () => toast('Friend request sent.'),
      onError: (err) => toast((err as Error).message || 'Could not send friend request', 'error'),
    });
  }

  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
  const avatarSrc = profile?.avatar_url ?? str(meta.avatar_url) ?? str(meta.picture);
  const accountName = profile?.display_name ?? str(meta.full_name) ?? str(meta.name) ?? user?.email ?? null;

  const accountControl = !authLoading &&
    (user ? (
      <AvatarMenu
        avatarSrc={avatarSrc}
        displayName={accountName}
        email={user.email}
        onViewProfile={() => navigate(ROUTES.profileSelf)}
        onSettings={() => navigate(ROUTES.settings)}
        onLogout={() => void signOut()}
        dropdownClassName="bg-[rgba(12,14,20,0.96)] shadow-[0_24px_70px_rgba(0,0,0,0.75)] backdrop-blur-3xl"
      />
    ) : (
      <button
        onClick={openAuth}
        aria-label="Sign in"
        className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white/15 text-hi transition-colors hover:border-white/35"
      >
        <UserIcon size={18} strokeWidth={2} />
      </button>
    ));

  return (
    <div className="fixed inset-0 z-[120] overflow-hidden bg-[linear-gradient(180deg,rgba(10,10,14,0.18),rgba(10,10,14,0.3))] backdrop-blur-[22px] backdrop-saturate-[1.3]">
      {/* Header — break line stops before the rail so the chat's day divider pops. */}
      <div className="pointer-events-none absolute left-0 right-0 top-0 z-[20] flex h-16 items-center border-b border-white/[0.06] bg-gradient-to-b from-[rgba(12,13,18,0.72)] via-[rgba(12,13,18,0.4)] to-transparent px-5 backdrop-blur-md lg:right-[380px]">
        <div className="relative z-[2] flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="pointer-events-auto h-10 w-10 rounded-full px-0"
            onClick={() => navigate(-1)}
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </Button>
          {canAddFriend && (
            <Button
              variant="ghost"
              size="sm"
              className="pointer-events-auto h-10 w-10 rounded-full px-0 text-accent"
              onClick={addAsFriend}
              disabled={addFriend.isPending}
              title="Add friend"
              aria-label="Add friend"
            >
              <UserPlus size={18} />
            </Button>
          )}
        </div>
        <div className="absolute inset-0 flex items-center justify-center text-center">
          <div className="min-w-0 max-w-[min(760px,calc(100vw-220px))] px-4">
            <div className="truncate text-[15px] font-extrabold tracking-[-0.2px] text-[#fafafa]">
              {peer ? `Chat with ${peerName}` : 'Private member chat'}
            </div>
          </div>
        </div>
        <div className="pointer-events-auto relative z-[2] ml-auto flex items-center gap-2 lg:hidden">
          <ToolkitMenu buttonClassName="h-10 w-10" iconSize={24} />
          {accountControl}
        </div>
      </div>

      <div className="absolute inset-0 grid min-h-0 lg:grid-cols-[minmax(0,1fr)_380px]">
        {loading ? (
          <div className="grid h-full place-items-center bg-black/20 lg:col-span-2">
            <Spinner />
          </div>
        ) : peer ? (
          <>
            <div className="min-h-0 pt-16">
              <DirectMessageThread conversationId={conversationId} otherProfile={peer} />
            </div>
            <div className="hidden min-h-0 border-l border-white/[0.06] lg:block">
              <FriendshipInfoRail
                profile={peer}
                conversationId={conversationId}
                topActions={
                  <>
                    <ToolkitMenu buttonClassName="h-9 w-9 drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]" iconSize={22} />
                    {accountControl}
                  </>
                }
              />
            </div>
          </>
        ) : (
          <div className="grid h-full place-items-center bg-black/20 text-sm text-mid lg:col-span-2">
            Conversation not found.
          </div>
        )}
      </div>
    </div>
  );
}
